/** Normalize and validate structured response blocks from AskResponseV1 payloads. */

export const STRUCTURED_BLOCK_TYPES = {
    EXECUTIVE_SUMMARY: 'executive_summary',
    KPI_CARDS: 'kpi_cards',
    RECOMMENDATIONS: 'recommendations',
    KEY_FINDINGS: 'key_findings',
    TIMELINE: 'timeline',
    CHECKLIST: 'checklist',
    COMPARISON_TABLE: 'comparison_table',
    RISK_MATRIX: 'risk_matrix',
    EXPANDABLE_SECTION: 'expandable_section',
    MARKDOWN: 'markdown',
};

const BLOCK_TYPE_ALIASES = {
    executive_summary: 'executive_summary',
    'executive-summary': 'executive_summary',
    summary: 'executive_summary',
    kpi_cards: 'kpi_cards',
    kpi: 'kpi_cards',
    kpis: 'kpi_cards',
    recommendations: 'recommendations',
    recommendation_list: 'recommendations',
    key_findings: 'key_findings',
    findings: 'key_findings',
    timeline: 'timeline',
    checklist: 'checklist',
    comparison_table: 'comparison_table',
    table: 'comparison_table',
    risk_matrix: 'risk_matrix',
    expandable_section: 'expandable_section',
    expandable: 'expandable_section',
    markdown: 'markdown',
};

export function normalizeBlockType(type) {
    const raw = String(type || '')
        .trim()
        .toLowerCase();
    return BLOCK_TYPE_ALIASES[raw] || raw || '';
}

export function normalizeResponseBlocks(raw) {
    const list = Array.isArray(raw) ? raw : [];
    return list
        .filter(block => block && typeof block === 'object')
        .map((block, index) => ({
            ...block,
            id: block.id || `block-${index}`,
            type: normalizeBlockType(block.type),
        }))
        .filter(block => block.type);
}

export function resolveResponseBlocks(data = {}) {
    if (!data || typeof data !== 'object') return [];
    return normalizeResponseBlocks(data.response_blocks ?? data.blocks);
}

export function hasStructuredBlocks(dataOrBlocks) {
    if (Array.isArray(dataOrBlocks)) {
        return normalizeResponseBlocks(dataOrBlocks).length > 0;
    }
    return resolveResponseBlocks(dataOrBlocks).length > 0;
}

export function buildStructuredBlocksCacheKey(blocks) {
    return normalizeResponseBlocks(blocks)
        .map(block => `${block.id}:${block.type}`)
        .join('|');
}

export function extractBlockMarkdown(block) {
    if (!block || typeof block !== 'object') return '';
    return String(block.content ?? block.markdown ?? block.body ?? block.text ?? '').trim();
}

export function extractBlockItems(block) {
    if (!block || typeof block !== 'object') return [];
    return Array.isArray(block.items) ? block.items.filter(Boolean) : [];
}

export function normalizePriorityLabel(priority) {
    const raw = String(priority || '').trim();
    return raw || null;
}

export function priorityTone(priority) {
    const value = String(priority || '').toLowerCase();
    if (value.includes('high') || value.includes('critical')) return 'high';
    if (value.includes('medium')) return 'medium';
    if (value.includes('low')) return 'low';
    return 'neutral';
}

export function resolveMessageResponseBlocks(messageData, lastAsk = null) {
    const fromMessage = resolveResponseBlocks(messageData);
    if (fromMessage.length > 0) return fromMessage;

    const traceId = messageData?.trace_id;
    if (!traceId || !lastAsk || lastAsk.trace_id !== traceId) return [];

    return resolveResponseBlocks(lastAsk);
}
